import React, { useEffect, useMemo, useRef } from "react";
import { formatDistanceToNowStrict } from "date-fns";

import { useEventDefs } from "@/api/useEventDefs";
import { RBACAccessLevel, RBACDomain } from "@/access-control/types";
import { Banner } from "@/design-system/components/Banner/Banner";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";
import { Typography } from "@/design-system/components/Typography/Typography";
import useListAllDestinationsForEnv from "@/destinations/v2/hooks/useListAllDestinationsForEnv";
import useCurrentEnvironment from "@/hooks/use-environment";
import { useUserHasAccess } from "@/hooks/useUserHasAccess";
import {
  LiveViewEvent,
  useLiveEventStream,
} from "@/LiveViewPage/liveViewConnection";
import { WizardLink } from "@/wizards/framework/WizardLink";
import { WizardStepLayout } from "@/wizards/framework/WizardStepLayout";
import { EnableDestinationStepProps } from "../stepProps";
import { useBatchedRecentItems } from "../useBatchedRecentItems";
import { useEventsForDestination } from "../useEventsForDestination";
import { useHasWaited } from "../useHasWaited";

const MAX_MATCHES = 20;
const MATCH_RENDER_INTERVAL_MS = 1000;
const GIVE_UP_AFTER_MS = 2 * 60_000;

type MatchedEvent = LiveViewEvent & { key: string; receivedAt: number };

export function LiveViewStep({
  data,
  context,
  updateData,
  goNext,
}: EnableDestinationStepProps) {
  const envID = useCurrentEnvironment()?.ID;
  const events = useEventsForDestination(data, context);
  const { isLoading: eventDefsLoading } = useEventDefs();
  const { isLoading: destinationsLoading } = useListAllDestinationsForEnv();
  const canReadPHI = useUserHasAccess(RBACDomain.phi, RBACAccessLevel.read);
  const { items: matches, add: addMatch } = useBatchedRecentItems<MatchedEvent>(
    MAX_MATCHES,
    MATCH_RENDER_INTERVAL_MS,
  );
  // Every updateData call rewrites wizard storage and syncs other tabs, so only
  // the first match may record liveViewSeenAt.
  const seenRef = useRef(!!data.liveViewSeenAt);
  useEffect(() => {
    seenRef.current = !!data.liveViewSeenAt;
  }, [data.liveViewSeenAt]);

  const eventIds = useMemo(() => new Set(events.map((e) => e.id)), [events]);
  const isLoading =
    eventDefsLoading || destinationsLoading || canReadPHI === null;
  const isWatching = canReadPHI === true && events.length > 0;

  useLiveEventStream(
    envID,
    (event) => {
      if (!event.event_def_id || !eventIds.has(event.event_def_id)) {
        return;
      }
      const receivedAt = Date.now();
      addMatch({ ...event, key: `${receivedAt}-${Math.random()}`, receivedAt });
      if (seenRef.current) {
        return;
      }
      seenRef.current = true;
      updateData((latest) =>
        latest.liveViewSeenAt ? {} : { liveViewSeenAt: receivedAt },
      );
    },
    isWatching,
  );

  const seen = !!data.liveViewSeenAt;
  const waitedTooLong = useHasWaited(GIVE_UP_AFTER_MS, isWatching && !seen);

  function renderBody() {
    if (isLoading) {
      return <LoadingSpinner className="size-6" />;
    }
    if (canReadPHI === false) {
      return (
        <Banner type="warning">
          Live events can include PHI, and you don’t have permission to view it.
          Ask an admin to check that events are firing, or continue without
          checking.
        </Banner>
      );
    }
    if (events.length === 0) {
      return (
        <Banner type="warning">
          No events are set to send to this destination yet. Go back and turn
          one on.
        </Banner>
      );
    }
    return (
      <>
        <div className="flex flex-col gap-2">
          <Typography variant="label-sm">Watching for</Typography>
          <Typography variant="body-sm" color="subtle">
            {events.map((e) => e.name).join(", ")}
          </Typography>
        </div>
        {data.liveViewSeenAt ? (
          <Banner type="success">
            Freshpaint is receiving your events. The first arrived{" "}
            {formatDistanceToNowStrict(data.liveViewSeenAt, {
              addSuffix: true,
            })}
            .
          </Banner>
        ) : (
          <div className="flex items-center gap-3">
            <LoadingSpinner className="size-5" />
            <Typography variant="body-sm" color="subtle">
              Waiting for events…
            </Typography>
          </div>
        )}
        {!seen && waitedTooLong && (
          <Banner type="warning">
            <div>
              None of these events have arrived yet. Common causes:
              <ul className="mt-1 list-disc pl-4">
                <li>
                  The Freshpaint snippet isn’t installed on the page you’re
                  testing.
                </li>
                <li>
                  An ad blocker is blocking Freshpaint. Try a private window
                  without extensions.
                </li>
                <li>
                  The event’s definition doesn’t match what you did. Check it in
                  the{" "}
                  <WizardLink
                    to="/events/library"
                    className="cursor-pointer underline"
                  >
                    Event Library
                  </WizardLink>
                  .
                </li>
              </ul>
            </div>
          </Banner>
        )}
        {matches.length > 0 && (
          <ul className="border-borderSubtle divide-borderSubtle divide-y rounded-lg border">
            {matches.map((match) => (
              <li
                key={match.key}
                className="flex items-center justify-between gap-4 px-4 py-2"
              >
                <span className="text-label-sm text-textPrimary truncate">
                  {match.event_name}
                </span>
                <span className="text-body-xs text-textTertiary truncate">
                  {match.path}
                </span>
                <span className="text-body-xs text-textTertiary shrink-0">
                  {formatDistanceToNowStrict(match.receivedAt, {
                    addSuffix: true,
                  })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </>
    );
  }

  return (
    <WizardStepLayout
      title="Check that events are firing"
      description="Trigger one of the events on your site or app. It shows up here as soon as Freshpaint receives it."
      primaryAction={{
        label: seen ? "Continue" : "Continue without checking",
        onClick: goNext,
      }}
    >
      {renderBody()}
      <Typography variant="body-sm">
        <WizardLink
          to="/events/liveview"
          className="text-textActionSelected cursor-pointer underline"
        >
          Open Live View
        </WizardLink>{" "}
        to see every incoming event.
      </Typography>
    </WizardStepLayout>
  );
}
